/**
 * Cynthia Pamplona — <cp-store-locator> (Figma "Nossas lojas › Localizador").
 * Filtro por estado (select) e seleção de loja: o card escolhido fica ativo (aria-pressed) e a foto de fundo
 * troca para a foto da loja (data-photo) ou para a foto padrão da seção. A lista usa uma barra de rolagem
 * desenhada (trilho 4px + indicador grafite do Figma), sincronizada com o scroll e arrastável.
 */
class CpStoreLocator extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.filter?.addEventListener('change', () => this.applyFilter(), { signal });

    for (const card of this.cards) {
      card.addEventListener('click', (event) => {
        // links do card (WhatsApp, mapa) seguem normalmente
        if (event.target instanceof Element && event.target.closest('a')) return;
        this.select(card);
      }, { signal });
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target !== card) return;
        event.preventDefault();
        this.select(card);
      }, { signal });
    }

    this.list?.addEventListener('scroll', () => this.updateScrollbar(), { signal, passive: true });
    this.thumb?.addEventListener('pointerdown', (event) => this.#dragThumb(event), { signal });
    this.scrollbar?.addEventListener('pointerdown', (event) => {
      if (event.target !== this.scrollbar || !this.list) return;
      // clique no trilho: rola uma "página" na direção do clique
      const above = event.offsetY < (this.thumb?.offsetTop ?? 0) + this.#thumbOffset;
      this.list.scrollBy({ top: (above ? -1 : 1) * this.list.clientHeight * 0.9, behavior: 'smooth' });
    }, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.updateScrollbar());
    if (this.list) this.#resizeObserver.observe(this.list);
    this.updateScrollbar();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #thumbOffset = 0;

  /** @returns {HTMLElement | null} */
  get list() {
    return this.querySelector('[data-cp-store-list]');
  }

  /** @returns {HTMLElement | null} */
  get scrollbar() {
    return this.querySelector('.cp-store-locator__scrollbar');
  }

  /** @returns {HTMLElement | null} */
  get thumb() {
    return this.querySelector('.cp-store-locator__thumb');
  }

  updateScrollbar() {
    const { list, scrollbar } = this;
    if (!list || !scrollbar) return;

    const { scrollHeight, clientHeight, scrollTop } = list;
    const overflow = scrollHeight - clientHeight > 1;
    scrollbar.hidden = !overflow;
    if (!overflow) return;

    const track = clientHeight;
    const size = Math.max(40, Math.round((clientHeight / scrollHeight) * track));
    this.#thumbOffset = Math.round((scrollTop / (scrollHeight - clientHeight)) * (track - size));
    scrollbar.style.setProperty('--cp-thumb-size', `${size}px`);
    scrollbar.style.setProperty('--cp-thumb-offset', `${this.#thumbOffset}px`);
  }

  /** @param {PointerEvent} event */
  #dragThumb(event) {
    const { list, thumb } = this;
    if (!list || !thumb) return;
    event.preventDefault();

    const startY = event.clientY;
    const startScroll = list.scrollTop;
    const ratio = (list.scrollHeight - list.clientHeight) / (list.clientHeight - thumb.offsetHeight);
    thumb.setPointerCapture(event.pointerId);

    /** @param {PointerEvent} move */
    const onMove = (move) => {
      list.scrollTop = startScroll + (move.clientY - startY) * ratio;
    };
    const onUp = () => {
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', onUp);
      thumb.removeEventListener('pointercancel', onUp);
    };
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
  }

  /** @returns {HTMLSelectElement | null} */
  get filter() {
    return this.querySelector('[data-cp-store-filter]');
  }

  /** @returns {HTMLElement[]} */
  get cards() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cp-store]')]);
  }

  /** @returns {HTMLImageElement | null} */
  get image() {
    return this.querySelector('img.cp-store-locator__image');
  }

  applyFilter() {
    const uf = this.filter?.value ?? '';
    let first = null;
    for (const card of this.cards) {
      const visible = !uf || card.dataset.uf === uf;
      card.hidden = !visible;
      if (visible && !first) first = card;
    }
    this.list?.scrollTo({ top: 0 });
    this.updateScrollbar();
    if (first) this.select(first);
  }

  /** @param {HTMLElement} card */
  select(card) {
    for (const other of this.cards) {
      const active = other === card;
      other.setAttribute('aria-pressed', String(active));
      other.classList.toggle('cp-store-card--active', active);
    }

    const photo = card.dataset.photo || this.dataset.fallbackPhoto;
    const image = this.image;
    if (photo && image && image.getAttribute('src') !== photo) {
      image.style.opacity = '0';
      const next = new Image();
      next.onload = () => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = photo;
        image.style.opacity = '1';
      };
      next.src = photo;
    }
  }
}

if (!customElements.get('cp-store-locator')) customElements.define('cp-store-locator', CpStoreLocator);
